// QrGen.js
import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
    Box,
    Container,
    Grid,
    Typography,
    Card,
    CardContent,
    Divider,
    Alert,
    CircularProgress,
    Link as MLink,
} from "@mui/material";
import QrForm from "../components/QrForm";
import QrResult from "../components/QrResult";
import Footer from "../components/Footer";

/* Branding colors should match MainApp */
const BRAND_BLUE = "#0d396e";
const BRAND_GREEN = "#00C853";

function QrGen() {
    const [qrCode, setQrCode] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const backend = process.env.REACT_APP_BACKEND_URL;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", bgcolor: "#fff" }}>
            {/* HERO: logo above page title (same look & feel as main) */}
            <Box sx={{ pt: { xs: 6, md: 10 } }}>
                <Container maxWidth="lg">
                    <Box sx={{ textAlign: "center", mb: { xs: 4, md: 6 } }}>
                        <MLink component={RouterLink} to="/" underline="none">
                            <Box
                                component="img"
                                src={`${process.env.PUBLIC_URL}/images/helloqr.png`}
                                alt="HelloQR Logo"
                                sx={{
                                    width: { xs: 160, md: 200 },
                                    height: "auto",
                                    borderRadius: 2,
                                    display: "inline-block",
                                    mb: 2,
                                }}
                            />
                        </MLink>

                        <Typography
                            variant="h2"
                            component="h1"
                            sx={{ fontWeight: 800, color: BRAND_BLUE, lineHeight: 1.2 }}
                        >
                            QR Code Generator
                        </Typography>
                        <Typography variant="h6" sx={{ color: "text.secondary", mt: 1 }}>
                            Erstelle in Sekunden deinen individuellen QR Code – kostenlos & DSGVO-konform.
                        </Typography>
                    </Box>
                </Container>
            </Box>

            {/* CONTENT SECTION: two-column cards (form + result) */}
            <Box sx={{ bgcolor: "#f8fafc", py: { xs: 6, md: 10 }, flexGrow: 1 }}>
                <Container maxWidth="lg">
                    <Grid container spacing={3} alignItems="flex-start">
                        {/* Left: Form Card */}
                        <Grid item xs={12} md={6}>
                            <Card elevation={1} sx={{ borderRadius: 4 }}>
                                <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                                    <Typography variant="h5" sx={{ fontWeight: 700, color: BRAND_BLUE, mb: 1 }}>
                                        Eingaben
                                    </Typography>
                                    <Typography sx={{ color: "text.secondary", mb: 2 }}>
                                        Wähle den QR-Typ, gib die Inhalte ein und klicke auf „Generieren“.
                                    </Typography>
                                    <Divider sx={{ my: 2 }} />

                                    {/* Use your existing form component */}
                                    <QrForm
                                        setQrCode={setQrCode}
                                        setLoading={setLoading}
                                        setError={setError}
                                        backend={backend}
                                    />

                                    {/* Error feedback below form */}
                                    {error && (
                                        <Alert severity="error" sx={{ mt: 2 }}>
                                            {typeof error === "string" ? error : "Es ist ein Fehler aufgetreten."}
                                        </Alert>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Right: Result Card */}
                        <Grid item xs={12} md={6}>
                            <Card elevation={1} sx={{ borderRadius: 4, minHeight: { md: 420 } }}>
                                <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                                    <Typography variant="h5" sx={{ fontWeight: 700, color: BRAND_BLUE, mb: 1 }}>
                                        Ergebnis
                                    </Typography>
                                    <Typography sx={{ color: "text.secondary", mb: 2 }}>
                                        Vorschau & Download deines QR Codes.
                                    </Typography>
                                    <Divider sx={{ my: 2 }} />

                                    {/* Loading State */}
                                    {loading && (
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2 }}>
                                            <CircularProgress size={22} sx={{ color: BRAND_GREEN }} />
                                            <Typography>Lade …</Typography>
                                        </Box>
                                    )}

                                    {/* Result or Placeholder */}
                                    {!loading && qrCode ? (
                                        <QrResult qrCode={qrCode} loading={loading} error={error} />
                                    ) : (
                                        !loading && (
                                            <Box sx={{ mt: 2, color: "text.secondary" }}>
                                                <Typography>
                                                    Noch kein QR Code generiert. Bitte fülle das Formular aus und klicke auf
                                                    „Generieren“.
                                                </Typography>
                                            </Box>
                                        )
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* FOOTER */}
            <Footer />
        </Box>
    );
}

export default QrGen;
